<template>
  <div class="select" v-click-outside="close">
    <div class="select__header">
      <button
        type="button"
        class="select__toggle"
        @click="toggle"
        :class="{
          select__toggle_no_focus: isOpen,
          select__toggle_disabled: disabled
        }"
      >
        <template v-if="selectedOption">
          <span
            class="select__toggle-text select__toggle-text_has_selected_option"
          >
            {{ selectedOption.title }}
          </span>
        </template>

        <template v-else>
          <span class="select__toggle-text"> {{ placeholder }} </span>
        </template>

        <icon name="select-arrow" :class="{ rotated: isOpen }" />
      </button>
    </div>

    <div class="select__content">
      <div
        class="select__holder"
        v-if="isOpen"
        ref="holder"
        :class="{
          select__holder_position_to_top: isPositionToTop,
          select__holder_position_to_bottom: isPositionToBottom,
          select__holder_has_search: isSearchable
        }"
      >
        <div class="select__holder-search" v-if="isSearchable">
          <input
            type="text"
            aria-label="Поиск"
            placeholder="Поиск..."
            v-model="searchText"
          />

          <icon name="find" />
        </div>

        <simplebar class="select__holder-cs">
          <button
            type="button"
            v-for="option in searchedOptions"
            :key="option.value"
            class="select__holder-option"
            :class="{
              'select__holder-option_selected': option.value === currentValue
            }"
            @click="selectOption(option)"
          >
            {{ option.title }}
          </button>
        </simplebar>
      </div>
    </div>
  </div>
</template>

<script>
import { i18n } from "@v2/shared/config";

const PLACEHOLDER_DEFAULT_VALUE =
  i18n.locale === "en" ? "Choose from the list" : "Выберите из списка";

const POSITION_TO_BOTTOM = 1,
  POSITION_TO_TOP = 2;

export default {
  name: "Select",
  props: {
    placeholder: {
      type: String,
      default: PLACEHOLDER_DEFAULT_VALUE
    },
    options: {
      default: () => [],
      validator(options) {
        return !options.find(
          option =>
            !option.hasOwnProperty("value") || !option.hasOwnProperty("title")
        );
      }
    },
    value: {
      default: null
    },
    searchCount: {
      type: Number,
      default: 7
    },
    disabled: Boolean
  },

  data() {
    return {
      isOpen: false,
      position: null,
      currentValue: this.value,
      searchText: ""
    };
  },

  computed: {
    searchedOptions() {
      const searchText = this.searchText.toLowerCase().trim();

      if (!searchText) {
        return this.options;
      }

      return this.options.filter(
        x =>
          typeof x.title === "string" &&
          x.title.toLowerCase().indexOf(searchText) > -1
      );
    },

    selectedOption() {
      if (!this.currentValue) {
        return null;
      }

      const option = this.options.find(x => x.value === this.currentValue);

      if (!option) {
        return null;
      }

      return option;
    },

    isPositionToTop() {
      return this.position === POSITION_TO_TOP;
    },

    isPositionToBottom() {
      return this.position === POSITION_TO_BOTTOM;
    },

    isSearchable() {
      return this.options.length > this.searchCount;
    }
  },

  methods: {
    toggle() {
      this.isOpen ? this.close() : this.open();
    },

    open() {
      this.isOpen = true;
      this.searchText = "";

      this.calculateEdges();
    },

    close() {
      this.isOpen = false;
      this.position = null;
      this.searchText = "";
    },

    selectOption(option) {
      this.currentValue = option.value;
      this.$emit("input", option.value);
      this.close();
    },

    calculateEdges() {
      this.$nextTick(() => {
        const holder = this.$refs.holder;
        const { top, height } = holder.getBoundingClientRect();

        const distance1 = window.innerHeight - top;
        const distance2 = window.scrollY + top;

        if (distance1 >= height) {
          this.position = POSITION_TO_BOTTOM;
        } else if (top - height >= 0 || distance2 - height >= 0) {
          this.position = POSITION_TO_TOP;
        } else {
          this.position = POSITION_TO_BOTTOM;
        }
      });
    }
  },

  watch: {
    value(value) {
      this.currentValue = value;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.select {
  &__header {
    position: relative;
  }

  &__toggle {
    @include button-clean();

    width: 100%;
    height: 48px;
    box-sizing: border-box;

    border-radius: 2px;
    border: 1px solid #cdced0;

    background-color: #ffffff;

    text-align: left;

    padding: 0 16px;

    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: space-between;

    @include respond-down(md) {
      height: 40px;
      padding: 0 12px;
    }

    &-text {
      overflow: hidden;
      text-overflow: ellipsis;

      white-space: nowrap;

      width: calc(100% - 16px * 2);

      color: #cdced0;

      &_has_selected_option {
        color: #000;
      }
    }

    .svg-icon {
      width: 16px;
      height: 16px;

      flex-shrink: 0;

      color: #0070d2;

      transition: all 0.3s;

      &.rotated {
        transform: rotate(180deg);
      }
    }

    &:not(&_no_focus):hover,
    &:not(&_no_focus):focus {
      border-color: #a3a8b3;
    }

    &_disabled {
      pointer-events: none;
      user-select: none;
      touch-action: none;

      background-color: rgba(241, 241, 241, 0.5);
      border-color: rgba(205, 206, 208, 0.5);
    }

    &_disabled &-text {
      &,
      &_has_selected_option {
        color: #cdced0;
      }
    }
  }

  &__native-select {
    position: absolute;
    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    opacity: 0;

    display: none;
  }

  &__content {
    position: relative;
    z-index: 15;
  }

  &__holder {
    position: absolute;

    &_position_to_top {
      bottom: 0;
    }

    width: 100%;
    max-height: 300px;

    &_has_search {
      max-height: 350px;
    }

    border-radius: 2px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
    background-color: #ffffff;

    margin-top: 8px;

    &-cs {
      max-height: 300px;
      position: relative;
    }

    &-search {
      position: relative;

      input {
        background: #fff;

        box-sizing: border-box;

        width: 100%;
        height: 50px;

        padding: 0 40px 0 16px;

        border: none;
        border-bottom: 1px solid rgba(#cdced0, 0.5);

        outline: none;
      }

      .svg-icon {
        position: absolute;
        right: 16px;
        top: 50%;

        transform: translateY(-50%);

        pointer-events: none;
        user-select: none;

        width: 16px;
        height: 16px;

        color: #a3a8b3;
      }

      input:not(:placeholder-shown) {
        border-bottom-color: #cdced0;

        & + .svg-icon {
          color: #005197;
        }
      }
    }

    &-option {
      @include button-clean();

      width: 100%;
      box-sizing: border-box;

      padding: 10px 16px;

      font-size: 13px;
      line-height: 1.54;
      color: #000000;

      text-align: left;

      transition: all 0.3s;

      &:hover,
      &:focus,
      &_selected {
        background-color: #009ee3;
        color: #fff;
      }

      &_selected:hover,
      &_selected:focus {
        background-color: rgba(#009ee3, 0.9);
      }
    }
  }
}
</style>
